import React, { useState } from 'react';
import UnitStatusChip from '../UnitStatusChip';
import {
  CONFIDENCE_TONE,
  CONFIDENCE_TONE_FALLBACK,
  AVAILABILITY_VIEW,
  AVAILABILITY_VIEW_FALLBACK,
  AVAILABILITY_REASON_AR,
  AVAILABILITY_REASON_UNKNOWN,
  CLEANING_VIEW,
  CLEANING_VIEW_FALLBACK,
  CLEANING_REASON_AR,
  CLEANING_REASON_UNKNOWN,
  CLEANING_COMPLETED_LABEL,
  CLEANING_TICKET_LABEL,
  OPERATIONAL_STATUS_LABEL,
  OPERATIONAL_STATUS_HINT,
  ENTRY_FROM_AR,
  ENTRY_SECTION_LABEL,
  STAY_SECTION_LABEL,
  AVAILABILITY_SECTION_LABEL,
  CLEANING_SECTION_LABEL,
  NOT_A_HOLD_AR,
  OBSERVED_AT_LABEL,
  NIGHTS_AR
} from '../../config/prebookingLabels';
import {
  formatTime,
  formatShortDate,
  formatRelativeTime,
  formatShortDateTime
} from '../../utils/dateFormatter';
import { formatCount } from '../../utils/numberFormatter';

// PRE-BOOKING READINESS — the second shape of the inbox context line.
// Spec: tasks/pre-booking-context-panel.md (increment 4) · resolver:
// be/services/prebooking/readinessResolver.js.
//
// A guest with NO booking asks, usually at 04:00, whether they can book and come now. Answering
// meant leaving the inbox for the calendar and the task list. This renders the three facts that
// answer it — is the unit free, is it clean, when does the door open — in one line, with the
// working out behind a tap.
//
// ⚠️ IT FORMS NO VERDICT. Every status, reason and rollup arrives decided from the resolver; this
// file chooses words and colours for them and nothing else. Its WORDS live in
// config/prebookingLabels.js for the same reason the arrival panel's do — agreeing on the data
// while retyping the sentences is drift that never shows up in a diff.
//
// 🔴 IT NEVER SAYS "COME NOW" (D103). The door code's validity window derives from the property's
// check-in time, so the lock physically cannot admit a guest before it — an enquiry at 04:00 cannot
// enter until ~14:00 however the message is worded. The line therefore STATES «الدخول من ٣:٠٠ م»
// and the policy text is DISPLAYED, never interpreted into an offer.
//
// 🎨 COLOUR IS NEVER THE ONLY CHANNEL. The dot is the D100 traffic light and it is aria-hidden; the
// verdict is always spelled out beside it, and an amber always says WHY it is amber rather than
// leaving an operator to read "minor" into a colour.

/**
 * The sentence on the collapsed line.
 *
 * Availability leads because it is load-bearing — without it there is no answer to give, whatever
 * the cleaning says. The cleaning word is appended ONLY when it is what makes the badge amber, so
 * the line explains its own colour: «متاحة للحجز · النظافة غير معروفة» rather than an unexplained
 * orange dot that reads as a minor caveat.
 *
 * D101 lives here: an UNKNOWN cleaning state is printed as plainly as a dirty one. The two are
 * different answers and neither is a soft yes.
 */
const summarise = (availability, cleaning) => {
  const availabilityText = AVAILABILITY_VIEW[availability?.status] || AVAILABILITY_VIEW_FALLBACK;

  // Only when the unit is genuinely offerable does cleaning change the reading. On UNAVAILABLE or
  // UNKNOWN availability the badge is already red and the cleaning word would be noise on a unit
  // nobody can sell tonight — it is still in the body for anyone who needs it.
  if (availability?.status !== 'AVAILABLE') return { availabilityText, cleaningText: null };

  // READY needs no words (the badge is green) and NOT_APPLICABLE is not a caveat — a stay three
  // weeks out has nothing to clean yet. Both fall through to a bare availability sentence.
  if (cleaning?.status === 'READY' || cleaning?.status === 'NOT_APPLICABLE') {
    return { availabilityText, cleaningText: null };
  }

  const view = CLEANING_VIEW[cleaning?.status] || CLEANING_VIEW_FALLBACK;
  return { availabilityText, cleaningText: view.standalone || view.label };
};

const PreBookingContextLine = ({ context }) => {
  const [expanded, setExpanded] = useState(false);

  const { availability, cleaning, entry, unitStatus, confidence, observedAt, dates } = context;

  const tone = CONFIDENCE_TONE[confidence] || CONFIDENCE_TONE_FALLBACK;
  const { availabilityText, cleaningText } = summarise(availability, cleaning);

  // A wall-clock HH:MM string, so `formatTime` and never an instant formatter — the property's
  // check-in time is 15:00 in Riyadh regardless of where the browser is.
  const checkInTimeText = ENTRY_FROM_AR(formatTime(entry?.checkInTime));

  const availabilityReason = availability?.reason
    ? (AVAILABILITY_REASON_AR[availability.reason] || AVAILABILITY_REASON_UNKNOWN)
    : null;
  const cleaningView = CLEANING_VIEW[cleaning?.status] || CLEANING_VIEW_FALLBACK;
  const cleaningReason = cleaning?.reason
    ? (CLEANING_REASON_AR[cleaning.reason] || CLEANING_REASON_UNKNOWN)
    : null;

  const nights = availability?.nights || 0;

  return (
    <div className="inbox-context">
      {/* No `aria-label`: it would REPLACE the contents in the accessible name, and a screen-reader
          user would hear the affordance instead of the two facts the line exists to deliver.
          `aria-expanded` already carries the expand semantic and `aria-controls` names the region. */}
      <button
        type="button"
        className="inbox-context-line inbox-prebooking-line"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="inbox-prebooking-body"
      >
        <span className={`inbox-context-verdict ${tone}`}>
          <span className="inbox-context-dot" aria-hidden="true" />
          <span className="inbox-context-verdict-text">{availabilityText}</span>
          {cleaningText && (
            <span className="inbox-context-verdict-sub">· {cleaningText}</span>
          )}
        </span>

        {/* THE ANSWER TO «يمديني احجز الان وادخل», and it is on the LINE rather than behind the tap
            on purpose — it is the fact the whole panel exists to put in front of someone at 04:00. */}
        <span className="inbox-prebooking-entry">{checkInTimeText}</span>

        {/* D102 — HOW STALE THIS READING IS. The panel does not refresh, so a thread opened at 04:32
            and answered at 04:50 is quoting a 04:32 calendar; the stamp is what makes that visible,
            and it is the dispute record if the unit is taken in between.
            An icon rather than a word because a bare second time beside «الدخول من ٣:٠٠ م» would
            read as a second check-in time — and the label is still there for screen readers. */}
        {observedAt && (
          <span className="inbox-prebooking-observed">
            <i className="bi bi-clock-history" aria-hidden="true" />
            <span className="visually-hidden">{OBSERVED_AT_LABEL}</span>
            <time dateTime={observedAt}>{formatRelativeTime(observedAt)}</time>
          </span>
        )}

        <i
          className={`bi bi-chevron-down inbox-context-chevron${expanded ? ' is-open' : ''}`}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div className="inbox-context-body" id="inbox-prebooking-body">
          <section className="inbox-context-section">
            <h3 className="inbox-context-heading">{AVAILABILITY_SECTION_LABEL}</h3>
            <p className="inbox-context-value">{availabilityText}</p>
            {availabilityReason && <p className="inbox-context-hint">{availabilityReason}</p>}

            {/* WHICH DATES WE ACTUALLY CHECKED. Not duplication of the thread header: a dateless
                enquiry — common on Airbnb — is answered for TONIGHT by the resolver's default, and
                an operator who does not know that could quote a one-night answer at a guest asking
                about next month. Where the enquiry did carry dates this simply confirms them. */}
            {dates?.checkIn && dates?.checkOut && (
              <p className="inbox-context-hint">
                {STAY_SECTION_LABEL}:{' '}
                {/* Business dates, so the date-only formatter — never an instant formatter. */}
                <bdi>{formatShortDate(dates.checkIn)}</bdi>
                {' – '}
                <bdi>{formatShortDate(dates.checkOut)}</bdi>
                {/* `bdi` on the count as well as the dates. Measured in the browser: the order is
                    the same with and without it here, because every run is Arabic — so this is
                    insurance, not a fix. It is worth keeping because the sentence is one dot away
                    from the trap `formatProgress` documents (digits joined by a separator form ONE
                    number run, and number runs always render left-to-right), and because the next
                    person to add a latin value to this line should find the isolation already in
                    place rather than discover the rule the hard way. */}
                {nights > 0 && <> · <bdi>{NIGHTS_AR(formatCount(nights), nights)}</bdi></>}
              </p>
            )}

            {/* Said in full, once: this is a reading, not a reservation. */}
            {observedAt && (
              <p className="inbox-context-hint">
                {OBSERVED_AT_LABEL}: <bdi>{formatShortDateTime(observedAt)}</bdi> — {NOT_A_HOLD_AR}
              </p>
            )}
          </section>

          <section className="inbox-context-section">
            <h3 className="inbox-context-heading">{CLEANING_SECTION_LABEL}</h3>
            <p className="inbox-context-value">{cleaningView.label}</p>
            {cleaningReason && <p className="inbox-context-hint">{cleaningReason}</p>}
            {/* WHEN, AND WHICH TICKET — both labelled. A turnover finished five weeks ago still
                grades READY (the resolver takes the most recent one inside a 60-day window), so the
                date is what lets an operator judge the verdict rather than take it on trust. Every
                value is `bdi`-isolated: a latin ticket code and a date sitting in one RTL sentence
                are exactly where bidi reordering silently rewrites what is on screen. */}
            {cleaning?.completedAt && (
              <p className="inbox-context-hint">
                {CLEANING_COMPLETED_LABEL}:{' '}
                {/* An instant — when the task was actually finished. */}
                <bdi>{formatShortDateTime(cleaning.completedAt)}</bdi>
              </p>
            )}
            {cleaning?.ticketId && (
              <p className="inbox-context-hint">
                {CLEANING_TICKET_LABEL}: <bdi>{cleaning.ticketId}</bdi>
              </p>
            )}
          </section>

          <section className="inbox-context-section">
            <h3 className="inbox-context-heading">{ENTRY_SECTION_LABEL}</h3>
            <p className="inbox-context-value">{checkInTimeText}</p>

            {/* THE EARLY CHECK-IN LINE WAS REMOVED HERE on 2026-08-26, with the free-text field it
                rendered (D157, tasks/structured-early-checkin-policy.md). Left as a comment rather
                than silently deleted, because the panel is dark and whoever revives it needs to know
                a line is MISSING, not that it was never wanted.

                ⚠️ Do not restore prose. The replacement is the structured per-apartment state, and
                it must be resolved through `resolveEarlyCheckIn` (be/config/earlyCheckIn.js) and
                surfaced by the resolver — this component must not read the property directly.

                ⚠️ Hold the D155 line when wording it: the field says whether we are WILLING to allow
                early entry, NEVER that the apartment is ready today. Readiness is occupancy plus
                cleaning, and it is already computed separately above. The Arabic vocabulary is
                waiting in config/prebookingLabels.js (EARLY_CHECKIN_LABEL, and the deliberate
                «لا توجد سياسة مسجلة» which must never become «لا يوجد دخول مبكر» — D154). */}
          </section>

          {/* D104 — CONTEXT, NEVER A VERDICT. The derived «حالة التشغيل» of the unit NOW (same answer
              as the unit page); the verdict above is about the enquired dates, so the hint keeps the
              two apart. Null when it could not be worked out — then nothing is shown. */}
          {unitStatus && (
            <section className="inbox-context-section">
              <h3 className="inbox-context-heading">{OPERATIONAL_STATUS_LABEL}</h3>
              <p className="inbox-context-value">
                <UnitStatusChip unitStatus={unitStatus} showReason />
              </p>
              <p className="inbox-context-hint">{OPERATIONAL_STATUS_HINT}</p>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default PreBookingContextLine;
